// Promotions de la semaine - Page pour les ACHETEURS
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Percent, ShoppingCart, Package, ArrowLeft, Search, Filter,
  Star, Store, Clock, Zap, Tag, ChevronRight, Heart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Carte produit en promo
function PromoProductCard({ product, onAddToCart, onToggleFavorite }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group"
    >
      {/* Image */}
      <div className="relative aspect-square bg-gray-100">
        {product.image ? (
          <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={48} className="text-gray-300" />
          </div>
        )}
        {/* Badge promo */}
        <span className="absolute top-2 left-2 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-lg shadow-lg">
          -{product.discount}%
        </span>
        {/* Bouton favori */}
        <button
          onClick={() => onToggleFavorite(product)}
          className="absolute top-2 right-2 p-2 bg-white/90 hover:bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all"
        >
          <Heart size={18} className={product.isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'} />
        </button>
        {/* Temps restant */}
        {product.endsIn && (
          <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1">
            <Clock size={12} />
            <span>Fin dans {product.endsIn}</span>
          </div>
        )}
      </div>

      {/* Contenu */}
      <div className="p-4">
        {/* Catégorie */}
        <span className="text-xs text-orange-600 font-medium">{product.category}</span>
        
        <h3 className="font-medium text-gray-900 line-clamp-2 mb-2 mt-1 min-h-[48px]">
          {product.title}
        </h3>

        {/* Prix */}
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-bold text-red-600">{product.price.toFixed(2)} €</span>
          <span className="text-sm text-gray-400 line-through">{product.originalPrice.toFixed(2)} €</span>
        </div>

        {/* Économie */}
        <div className="bg-green-50 text-green-700 text-sm px-3 py-1.5 rounded-lg mb-3 inline-block">
          🎉 Économisez {(product.originalPrice - product.price).toFixed(2)} €
        </div>

        {/* Vendeur */}
        <div className="flex items-center gap-2 mb-4">
          <Store size={14} className="text-gray-400" />
          <span className="text-sm text-gray-500">{product.seller}</span>
          <div className="flex items-center gap-0.5 ml-auto">
            <Star size={12} className="text-yellow-400 fill-yellow-400" />
            <span className="text-xs text-gray-600">{product.rating}</span>
          </div>
        </div>

        {/* Bouton */}
        <Button
          onClick={() => onAddToCart(product)}
          className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl"
        >
          <ShoppingCart size={16} className="mr-2" />
          Ajouter au panier
        </Button>
      </div>
    </motion.div>
  );
}

export default function PromotionsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'Toutes les promos' },
    { id: 'outillage', label: 'Outillage' },
    { id: 'electricite', label: 'Électricité' },
    { id: 'plomberie', label: 'Plomberie' },
    { id: 'peinture', label: 'Peinture' },
    { id: 'jardin', label: 'Jardin' },
  ];

  useEffect(() => {
    fetchProducts();
  }, [category]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Données simulées
      setProducts([
        {
          id: 'promo-1',
          title: 'Perceuse visseuse Bosch Professional 18V',
          price: 129,
          originalPrice: 179,
          discount: 28,
          category: 'Outillage électroportatif',
          seller: 'Outillage Pro Plus',
          rating: 4.8,
          endsIn: '2 jours',
          image: null
        },
        {
          id: 'promo-2',
          title: 'Lot de 100 chevilles + vis Fischer',
          price: 19.90,
          originalPrice: 34.90,
          discount: 43,
          category: 'Fixation',
          seller: 'BricoMax',
          rating: 4.6,
          endsIn: '5 jours',
          image: null
        },
        {
          id: 'promo-3',
          title: 'Niveau laser Bosch GLL 2-15',
          price: 89,
          originalPrice: 129,
          discount: 31,
          category: 'Mesure',
          seller: 'ElectroPro',
          rating: 4.9,
          endsIn: '3 jours',
          image: null
        },
        {
          id: 'promo-4',
          title: 'Peinture acrylique blanche 10L - Mat',
          price: 39.90,
          originalPrice: 59.90,
          discount: 33,
          category: 'Peinture',
          seller: 'PeintureDiscount',
          rating: 4.5,
          endsIn: '4 jours',
          image: null
        },
        {
          id: 'promo-5',
          title: 'Coffret clés à cliquet Facom 72 pièces',
          price: 189,
          originalPrice: 279,
          discount: 32,
          category: 'Outillage à main',
          seller: 'Outillage Pro Plus',
          rating: 4.9,
          endsIn: '2 jours',
          image: null
        },
        {
          id: 'promo-6',
          title: 'Meuleuse angulaire Makita 125mm',
          price: 69,
          originalPrice: 99,
          discount: 30,
          category: 'Outillage électroportatif',
          seller: 'BricoMax',
          rating: 4.7,
          endsIn: '6 jours',
          image: null
        },
        {
          id: 'promo-7',
          title: 'Robinet mitigeur cuisine inox',
          price: 49,
          originalPrice: 79,
          discount: 38,
          category: 'Plomberie',
          seller: 'SaniPro',
          rating: 4.4,
          image: null
        },
        {
          id: 'promo-8',
          title: 'Escabeau aluminium 4 marches',
          price: 59,
          originalPrice: 89,
          discount: 34,
          category: 'Échelles',
          seller: 'BricoMax',
          rating: 4.6,
          endsIn: '3 jours',
          image: null
        },
      ]);
    } catch (e) {
      console.error('Error fetching products:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    toast.success(`${product.title} ajouté au panier à ${product.price}€ !`);
  };

  const handleToggleFavorite = (product) => {
    toast.success(product.isFavorite ? 'Retiré des favoris' : 'Ajouté aux favoris');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="promotions-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-600 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Percent size={28} />
                Promotions de la semaine
              </h1>
              <p className="text-white/80">Jusqu'à -50% sur une sélection de produits</p>
            </div>
          </div>
        </div>
      </header>

      {/* Bannière */}
      <div className="bg-yellow-400 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-2 text-yellow-900">
            <Zap size={18} className="animate-pulse" />
            <span className="font-bold">OFFRES LIMITÉES</span>
            <span>•</span>
            <span>Plus de 50 produits en promotion cette semaine !</span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Catégories */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                category === cat.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-orange-300'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Grille de produits */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((product) => (
              <PromoProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>
        )}

        {/* CTA Newsletter */}
        <div className="mt-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-8 text-center text-white">
          <Tag size={32} className="mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-2">Ne ratez aucune promo !</h3>
          <p className="text-white/80 mb-6">Recevez nos meilleures offres chaque semaine</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Votre email"
              className="flex-1 rounded-xl bg-white/90 border-0 text-gray-900"
            />
            <Button className="bg-white text-orange-600 hover:bg-white/90 rounded-xl font-bold">
              S'abonner
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
